import React, { useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { Delete } from 'lucide-react';

interface DNIKeypadProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  initialValue?: string;
  maxLength?: number;
}

export const DNIKeypad: React.FC<DNIKeypadProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValue = '',
  maxLength = 8
}) => {
  const [displayValue, setDisplayValue] = useState<string>(initialValue);

  const handleNumberClick = (number: string) => {
    if (displayValue.length < maxLength) {
      setDisplayValue(displayValue + number);
    }
  };

  const handleBackspace = () => {
    if (displayValue.length > 0) {
      setDisplayValue(displayValue.slice(0, -1));
    }
  };

  const handleClear = () => {
    setDisplayValue('');
  };

  const handleSubmit = () => {
    if (displayValue.length >= 8) { // DNI debe tener 8 dígitos
      onSubmit(displayValue);
      onClose();
    }
  };

  const handleClose = () => {
    setDisplayValue(initialValue);
    onClose();
  };

  // Reset display value when modal opens with new initial value
  React.useEffect(() => {
    if (isOpen) {
      setDisplayValue(initialValue);
    }
  }, [isOpen, initialValue]);

  const keypadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['C', '0', '⌫']
  ];

  const isSubmitDisabled = displayValue.length < 8;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg text-center">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Display */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <div className="text-right text-2xl font-bold text-gray-800 min-h-[2.5rem] flex items-center justify-end font-mono">
              {displayValue}
              {displayValue.length < maxLength && (
                <span className="animate-pulse ml-1">|</span>
              )}
            </div>
            <div className="text-xs text-gray-500 text-right mt-1">
              {displayValue.length}/{maxLength} dígitos
            </div>
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3">
            {keypadButtons.flat().map((button, index) => {
              const isNumber = !isNaN(parseInt(button));
              const isClear = button === 'C';
              const isBackspace = button === '⌫';
              const isZero = button === '0';

              return (
                <Button
                  key={index}
                  variant="outline"
                  className={`
                    h-12 text-lg font-semibold transition-all duration-150 active:scale-95
                    ${isNumber || isZero ? 'bg-white hover:bg-gray-50 border-gray-300' : ''}
                    ${isClear ? 'bg-red-50 hover:bg-red-100 border-red-200 text-red-700' : ''}
                    ${isBackspace ? 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700' : ''}
                  `}
                  onClick={() => {
                    if (isNumber || isZero) {
                      handleNumberClick(button);
                    } else if (isClear) {
                      handleClear();
                    } else if (isBackspace) {
                      handleBackspace();
                    }
                  }}
                  disabled={displayValue.length >= maxLength && (isNumber || isZero)}
                >
                  {isBackspace ? <Delete className="h-5 w-5" /> : button}
                </Button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="h-12 text-lg"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className={`h-12 text-lg ${
                isSubmitDisabled
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-macartur-primary hover:bg-macartur-primary/90'
              }`}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};